import { EmptyState } from '@agentic-flow-studio/shared/components/empty-state';
import { SplitPaneLayout } from '@agentic-flow-studio/shared/components/layout/SplitPaneLayout';
import { CreateTaskDialog } from '@agentic-flow-studio/shared/components/tasks/CreateTaskDialog';
import { EditStatusesDialog } from '@agentic-flow-studio/shared/components/tasks/EditStatusesDialog';
import { KanbanView } from '@agentic-flow-studio/shared/components/tasks/KanbanView';
import { TaskListTable } from '@agentic-flow-studio/shared/components/tasks/TaskListTable';
import { TaskSideloadContent } from '@agentic-flow-studio/shared/components/tasks/TaskSideloadContent';
import { TasksFilterBar } from '@agentic-flow-studio/shared/components/tasks/TasksFilterBar';
import { TasksViewSwitcher } from '@agentic-flow-studio/shared/components/tasks/TasksViewSwitcher';
import { useRecordRecentPage } from '@agentic-flow-studio/shared/hooks/useRecordRecentPage';
import { useSearchPreservingNavigate } from '@agentic-flow-studio/shared/hooks/useSearchPreservingNavigate';
import { useTaskListFilters } from '@agentic-flow-studio/shared/hooks/useTaskListFilters';
import { useTasksView } from '@agentic-flow-studio/shared/hooks/useTasksView';
import { getTerminalStatusKey } from '@agentic-flow-studio/shared/lib/task-list';
import { Button } from '@agentic-flow-studio/shared/ui/button';
import { CheckSquare, Plus, Settings } from 'lucide-react';
import * as React from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useWorkspaceMembers } from '@/hooks/useHarbor';
import { trpc } from '@/utils/trpc';

export default function TasksPage() {
  const navigate = useSearchPreservingNavigate();
  const location = useLocation();
  const { workspaceId, orgId, taskId } = useParams<{
    workspaceId: string;
    orgId: string;
    taskId: string;
  }>();
  const parsedTaskId = taskId ? parseInt(taskId, 10) : undefined;
  const isEditMode = location.pathname.includes('/edit');

  const meQuery = trpc.auth.me.useQuery();
  const activeWorkspace = meQuery.data?.workspaces.find((w) => w.id.toString() === workspaceId);
  const isReadOnly = activeWorkspace?.role === 'viewer';

  const tasksQuery = trpc.tasks.getAll.useQuery();
  const tasks = tasksQuery.data || [];

  const { data: members = [] } = useWorkspaceMembers();
  const statusesQuery = trpc.taskStatuses.list.useQuery();
  const statuses = React.useMemo(() => statusesQuery.data?.items || [], [statusesQuery.data]);
  const utils = trpc.useUtils();

  const { view, setView } = useTasksView();
  const [isEditStatusesOpen, setIsEditStatusesOpen] = React.useState(false);
  const [isCreateOpen, setIsCreateOpen] = React.useState(false);

  const filters = useTaskListFilters(tasks, statuses, meQuery.data?.uid, {
    assigneeScope: 'all',
    members,
  });
  const isLoading = tasksQuery.isLoading || (view === 'list' && meQuery.isLoading);

  const taskQuery = trpc.tasks.getById.useQuery({ id: parsedTaskId! }, { enabled: !!parsedTaskId });

  // The selected task lives in a split pane, so this fires as the user opens tasks from the
  // list rather than on a route component mount. The hook's settle delay keeps rapid
  // arrow-key skimming out of the history.
  useRecordRecentPage(
    taskQuery.data
      ? {
          key: `task:${taskQuery.data.id}`,
          type: 'task',
          label: taskQuery.data.label,
          description: taskQuery.data.status ?? undefined,
        }
      : null
  );

  const createMutation = trpc.tasks.create.useMutation({
    onSuccess: () => {
      toast.success('Task created');
      tasksQuery.refetch();
      setIsCreateOpen(false);
    },
    onError: (error) => {
      toast.error(`Failed to create task: ${error.message}`);
    },
  });

  const updateMutation = trpc.tasks.update.useMutation({
    onSuccess: () => {
      tasksQuery.refetch();
      taskQuery.refetch();
      if (parsedTaskId) {
        navigate(`/org/${orgId}/workspace/${workspaceId}/tasks/${parsedTaskId}`);
      } else {
        navigate(`/org/${orgId}/workspace/${workspaceId}/tasks`);
      }
    },
  });

  // Used by inline status changes (Kanban drag-and-drop, Assignee-view checkbox toggle,
  // "Mark as Complete" quick action) — these happen in place and must NOT navigate,
  // otherwise the current view (?view=kanban/assignee) gets silently reset to My Tasks.
  const quickUpdateMutation = trpc.tasks.update.useMutation({
    onSuccess: () => {
      tasksQuery.refetch();
    },
  });

  const deleteMutation = trpc.tasks.delete.useMutation({
    onSuccess: () => {
      tasksQuery.refetch();
      navigate(`/org/${orgId}/workspace/${workspaceId}/tasks`);
    },
  });

  const handleDelete = (id: number) => {
    if (isReadOnly) return;
    if (confirm('Are you sure you want to delete this task? This action cannot be undone.')) {
      deleteMutation.mutate({ id });
    }
  };

  return (
    <SplitPaneLayout
      isOpen={!!parsedTaskId}
      onClose={() => navigate(`/org/${orgId}/workspace/${workspaceId}/tasks`)}
      mainPaneClassName="overflow-hidden p-0 h-full"
      sidePanelContent={
        taskQuery.data ? (
          <TaskSideloadContent
            task={taskQuery.data}
            workspaceMembers={members}
            isSubmitting={updateMutation.isPending}
            isDeleting={deleteMutation.isPending}
            initialMode={isEditMode ? 'edit' : 'view'}
            onClose={() => navigate(`/org/${orgId}/workspace/${workspaceId}/tasks`)}
            onDelete={() => parsedTaskId && handleDelete(parsedTaskId)}
            onSubmit={(data) => {
              if (!parsedTaskId) return;
              updateMutation.mutate({ id: parsedTaskId, ...data });
            }}
            isReadOnly={isReadOnly}
          />
        ) : null
      }
    >
      <main className="flex-1 overflow-hidden p-6 lg:p-10 relative h-full flex flex-col min-h-0 bg-slate-50/50 dark:bg-slate-950/50">
        <div className="w-full h-full flex flex-col min-h-0 overflow-hidden">
          <div className="w-full shrink-0 space-y-6">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between shrink-0">
              <div>
                <h1 className="text-3xl font-bold tracking-tight">Tasks</h1>
                <p className="text-muted-foreground mt-1">
                  Track and manage actions across your workspace.
                </p>
              </div>
              <div className="flex items-center gap-2">
                {!isReadOnly && view === 'board' && (
                  <Button variant="outline" onClick={() => setIsEditStatusesOpen(true)}>
                    <Settings className="mr-2 h-4 w-4" />
                    Edit Statuses
                  </Button>
                )}
                {!isReadOnly && (
                  <Button className="w-full md:w-auto" onClick={() => setIsCreateOpen(true)}>
                    <Plus className="mr-2 h-4 w-4" />
                    Add Task
                  </Button>
                )}
              </div>
            </div>

            {!isLoading && tasks.length === 0 ? (
              <div className="py-12">
                <EmptyState
                  icon={CheckSquare}
                  title="No tasks yet"
                  description="Create and assign tasks to track action items, deadlines, and progress across your workspace."
                  action={
                    !isReadOnly ? (
                      <Button onClick={() => setIsCreateOpen(true)} className="gap-1.5">
                        <Plus className="h-4 w-4" />
                        <span>Add Task</span>
                      </Button>
                    ) : undefined
                  }
                />
              </div>
            ) : (
              <div className="shrink-0 space-y-4">
                <div className="flex items-center justify-between">
                  <TasksViewSwitcher value={view} onChange={setView} />
                </div>

                <TasksFilterBar
                  filters={filters}
                  statuses={statuses}
                  members={members}
                  showGroupBy={view === 'list'}
                  tasks={tasks}
                  currentUserId={meQuery.data?.uid}
                />
              </div>
            )}
          </div>

          {(isLoading || tasks.length > 0) && (
            <div className="mt-6 flex-1 overflow-y-auto min-h-0 pr-1 space-y-6 scrollbar-thin pb-4 w-full">
              {view === 'board' ? (
                <KanbanView
                  tasks={filters.filteredTasks}
                  statuses={statuses}
                  isLoading={isLoading}
                  isReadOnly={isReadOnly}
                  workspaceMembers={members}
                  hasActiveFilters={filters.hasActiveFilters}
                  onClearFilters={filters.resetFilters}
                  onCreateTask={() => setIsCreateOpen(true)}
                  onTaskClick={(id) =>
                    navigate(`/org/${orgId}/workspace/${workspaceId}/tasks/${id}`)
                  }
                  onStatusChange={(taskId, status) => {
                    quickUpdateMutation.mutate({ id: taskId, status });
                  }}
                />
              ) : (
                <TaskListTable
                  isLoading={isLoading}
                  isReadOnly={!!isReadOnly}
                  groupedTasksList={filters.groupedTasksList}
                  groupBy={filters.groupBy}
                  collapsedGroups={filters.collapsedGroups}
                  onToggleGroup={filters.toggleGroup}
                  statuses={statuses}
                  members={members}
                  selectedTaskId={parsedTaskId}
                  onTaskClick={(id) =>
                    navigate(`/org/${orgId}/workspace/${workspaceId}/tasks/${id}`)
                  }
                  onMarkComplete={(task) =>
                    quickUpdateMutation.mutate({
                      id: task.id,
                      status: getTerminalStatusKey(statuses),
                    })
                  }
                  onEditTask={(id) =>
                    navigate(`/org/${orgId}/workspace/${workspaceId}/tasks/edit/${id}`)
                  }
                  onDeleteTask={handleDelete}
                  onClearFilters={filters.resetFilters}
                  onCreateTask={() => setIsCreateOpen(true)}
                  hasActiveFilters={filters.hasActiveFilters}
                  emptyTitle={
                    filters.hasActiveFilters
                      ? undefined
                      : filters.scopedTasks.length === 0
                        ? 'No tasks assigned to you'
                        : undefined
                  }
                  emptyDescription={
                    filters.hasActiveFilters
                      ? undefined
                      : filters.scopedTasks.length === 0
                        ? "You don't have any open tasks assigned in this workspace."
                        : undefined
                  }
                />
              )}
            </div>
          )}
        </div>
      </main>

      <EditStatusesDialog
        isOpen={isEditStatusesOpen}
        onClose={() => setIsEditStatusesOpen(false)}
        statuses={statuses}
        onChanged={() => utils.taskStatuses.list.invalidate()}
      />

      <CreateTaskDialog
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onSubmit={(data) => createMutation.mutate(data)}
        initialTitle=""
        initialDescription=""
        workspaceMembers={members}
        isSubmitting={createMutation.isPending}
        invitePortal="skill-viper"
      />
    </SplitPaneLayout>
  );
}
